import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import Login from './login';
import Layout from './admin/layout';
import Dashboard from './admin/dashboard';
import DashboardGhd from './admin/dashboardGhd';
import { config } from './libs/config';
import Alarms from './admin/alarms';
import Protected from './admin/protected';
import ErrorPage from './errors';
import SignOut from './signout';
import Messages from './messages';
import IntegrationSources from './admin/sources';
import AlarmPriorities from './admin/alarmPriorities';
import AlarmTypes from './admin/alarmTypes';
import AlarmViewer from './alarmViewer';
import Settings from './admin/settings';
import Users from './admin/users';
import UserProfile from './admin/userProfile';
import UserProtected from './protectedUser';
import IntegrationRouting from './admin/routing';
import RoutingDetails from './admin/routingDetails';
import Lookups from './admin/lookups';
import AlarmGroups from './admin/alarmGroups';
import Locations from './admin/locations';
import Logs from './logs';
import SystemHealth from './admin/serviceHealth';
import AlarmGroupTypes from './admin/alarmGroupTypes';

// Which screen /user lands on, chosen once at module load from runtime config so one image
// serves a client wanting the passenger/occupancy view and a client wanting the alarm viewer.
//
// This belongs on /user, not /admin: it is the non-admin landing page, behind UserProtected
// rather than Protected, which is what lets a plain user reach it. An earlier revision swapped
// /admin's index instead, which both hid the standard dashboard from admins at a GHD site and
// put the passenger view behind site-admin/oem.
//
// The two are alternatives on the same route rather than two routes, because a deployment has
// one or the other - a passenger-counting site has no alarm sources to view, and vice versa.
// menuItems.js gates the matching menu entry off the same config value.
const USER_LANDING = { standard: AlarmViewer, ghd: DashboardGhd };
const UserLanding = USER_LANDING[config.dashboard] ?? AlarmViewer;

const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    element: <Login />,
  },
  {
    id: "user",
    path: "/user",
    element: (
      <UserProtected>
        <Layout />
      </UserProtected>
    ),
    children: [
      {
        index: true,
        element: <UserLanding />
      },
    ]
  },
  {
    id: "signout",
    path: "/signout",
    element: <SignOut />    
  },
  {
    id: "admin",
    path: "/admin",
    element: (
      <Protected>
        <Layout />
      </Protected>
    ),
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: "sources",
        element: <IntegrationSources />
      },
      {
        path: "routing",
        element: <IntegrationRouting />
      },
      {
        path: "routing-details",
        element: <RoutingDetails />
      },
      {
        path: "alarms",
        element: <Alarms />
      },
      {
        path: "alarmTypes",
        element: <AlarmTypes />
      },
      {
        path: "alarmPriorities",
        element: <AlarmPriorities />
      },
      {
        path: "alarmGroups",
        element: <AlarmGroups />
      },
      // Only under 'group' routing. Spread rather than rendered conditionally so the path
      // does not exist at all elsewhere - a typed URL lands on the not-found handling rather
      // than on a screen this console does not otherwise offer.
      ...(config.alarmRouting === 'group' ? [{
        path: "alarmGroupTypes",
        element: <AlarmGroupTypes />
      }] : []),
      {
        path: "settings",
        element: <Settings />
      },
      {
        path: "users",
        element: <Users />     
      },
      {
        path: "user-profile",
        element: <UserProfile />
      },
      {
        path: "lookups",
        element: <Lookups />
      },
      {
        path: "locations",
        element: <Locations />
      },
      {
        path: "messages",
        element: <Messages />
      },   
      {
        path: "logs",
        element: <Logs />
      },
      {
        path: "service-health",
        element: <SystemHealth />
      }
    ]
  },
  {
    id: "errors",
    path: "*",
    element: <ErrorPage />
  }
]);

function App() {
  return (
    <RouterProvider router={router} />
  );
}

export default App;
